import {
	API_TRANSLATION_LOCALE_CODES,
	type ApiTranslationLocaleCode,
} from '@/config/locales';
import { apiClient } from '@/services/apiClient';

export type EntityTranslationFields = Record<string, string>;
type OfficialLocaleCode = ApiTranslationLocaleCode;
export type EntityTranslationResult<T extends EntityTranslationFields> = Record<OfficialLocaleCode, T>;

const GEMINI_CHAT_ENDPOINT = '/gemini/chat';
const ENTITY_AUTO_TRANSLATE_TIMEOUT_MS = 6000;

type GeminiChatRequestDto = {
	prompt: string;
};

type GeminiChatResponseDto = {
	response: string;
};

function debugAutoTranslate(
	runId: string,
	hypothesisId: string,
	message: string,
	data: Record<string, unknown>
): void {
	if (process.env.NODE_ENV !== 'development') return;
	// #region agent log
	fetch('http://127.0.0.1:7671/ingest/86a97c59-29dc-4de8-9eda-f49b3d766117',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'804701'},body:JSON.stringify({sessionId:'804701',runId,hypothesisId,location:'src/lib/entityAutoTranslate.ts:debugAutoTranslate',message,data,timestamp:Date.now()})}).catch(()=>{});
	// #endregion
}

export class EntityAutoTranslateError extends Error {
	constructor(message: string, options?: { cause?: unknown }) {
		super(message);
		this.name = 'EntityAutoTranslateError';
		if (options && 'cause' in options) {
			(this as Error & { cause?: unknown }).cause = options.cause;
		}
	}
}

function normalizeSourceLanguage(language: string | undefined): OfficialLocaleCode {
	const base = (language || 'en').toLowerCase().split('-')[0];
	if (base === 'ko' || base === 'en' || base === 'ar' || base === 'id' || base === 'mn') return base;
	return 'en';
}

function fallbackResult<T extends EntityTranslationFields>(source: T): EntityTranslationResult<T> {
	return {
		ko: { ...source },
		en: { ...source },
		ar: { ...source },
		id: { ...source },
		mn: { ...source },
	};
}

function requiredFieldNames<T extends EntityTranslationFields>(source: T): (keyof T)[] {
	return (Object.keys(source) as (keyof T)[]).filter((key) => source[key].trim().length > 0);
}

function extractJsonObject(raw: string): unknown {
	const text = raw.trim();
	const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1] ?? text;
	const start = fenced.indexOf('{');
	if (start < 0) throw new Error('Gemini response does not contain JSON.');
	let inString = false;
	let escaped = false;
	let depth = 0;
	for (let i = start; i < fenced.length; i += 1) {
		const ch = fenced[i];
		if (escaped) {
			escaped = false;
			continue;
		}
		if (ch === '\\') {
			escaped = inString;
			continue;
		}
		if (ch === '"') {
			inString = !inString;
			continue;
		}
		if (inString) continue;
		if (ch === '{') depth += 1;
		if (ch === '}') {
			depth -= 1;
			if (depth === 0) return JSON.parse(fenced.slice(start, i + 1));
		}
	}
	throw new Error('Gemini response JSON is incomplete.');
}

function coerceTranslatedResult<T extends EntityTranslationFields>(
	parsed: unknown,
	source: T
): EntityTranslationResult<T> {
	const fallback = fallbackResult(source);
	const requiredKeys = requiredFieldNames(source);
	if (!parsed || typeof parsed !== 'object') {
		throw new Error('Gemini response is not a JSON object.');
	}
	const root = parsed as Record<string, unknown>;
	const out: EntityTranslationResult<T> = { ...fallback };
	for (const locale of API_TRANSLATION_LOCALE_CODES) {
		const row = root[locale];
		if (!row || typeof row !== 'object' || Array.isArray(row)) {
			throw new Error(`Gemini response is missing locale "${locale}".`);
		}
		const next = { ...fallback[locale] };
		for (const key of Object.keys(source) as (keyof T)[]) {
			const value = (row as Record<string, unknown>)[String(key)];
			if (typeof value === 'string' && value.trim()) {
				next[key] = value as T[keyof T];
			}
		}
		for (const key of requiredKeys) {
			const rawValue = (row as Record<string, unknown>)[String(key)];
			if (typeof rawValue !== 'string' || !rawValue.trim()) {
				throw new Error(`Gemini response is missing "${locale}.${String(key)}".`);
			}
		}
		out[locale] = next;
	}
	return out;
}

function extractGeminiResponseText(response: unknown): string {
	if (typeof response === 'string') return response;
	if (!response || typeof response !== 'object') return '';
	const body = response as Record<string, unknown>;
	for (const key of ['response', 'text', 'message', 'content', 'answer', 'result']) {
		const value = body[key];
		if (typeof value === 'string') return value;
	}
	return JSON.stringify(response);
}

export async function translateEntityFields<T extends EntityTranslationFields>(
	sourceLanguage: string | undefined,
	fields: T
): Promise<EntityTranslationResult<T>> {
	const fieldKeys = Object.keys(fields);
	const nonEmptyFieldKeys = fieldKeys.filter((keyName) => fields[keyName]?.trim());
	const source = normalizeSourceLanguage(sourceLanguage);
	const prompt = `You translate user-entered entity content for a medical simulation and LMS platform.
Return JSON only. No markdown, no commentary.
Source language: ${source}
Target languages: ko, en, ar, id, mn.
Keep the source language fields exactly as input.
Preserve HTML tags, URLs, placeholders such as {{name}}, {count}, %s, units such as °C, mmHg, %, SpO₂.
Do not add definitive diagnoses. Use medically safe, neutral, educational wording.
Arabic must be Modern Standard Arabic. Mongolian must be in Cyrillic script. Indonesian should use formal "Bahasa Indonesia" style. Keep layout-neutral text only; do not add RTL marks.
Output shape:
{
  "ko": { same field keys as input },
  "en": { same field keys as input },
  "ar": { same field keys as input },
  "id": { same field keys as input },
  "mn": { same field keys as input }
}
Input fields:
${JSON.stringify(fields)}`;

	try {
		debugAutoTranslate('pre-check', 'H1', 'Gemini translate request starting', {
			endpoint: GEMINI_CHAT_ENDPOINT,
			sourceLanguage: source,
			fieldKeys,
			nonEmptyFieldKeys,
		});
		if (process.env.NODE_ENV === 'development') {
			console.log('[entityAutoTranslate request]', { endpoint: GEMINI_CHAT_ENDPOINT, sourceLanguage: source, fields });
		}
		const requestBody: GeminiChatRequestDto = { prompt };
		const response = await apiClient.post<GeminiChatResponseDto>(GEMINI_CHAT_ENDPOINT, requestBody, {
			timeout: ENTITY_AUTO_TRANSLATE_TIMEOUT_MS,
		});
		const raw = extractGeminiResponseText(response);
		if (!raw || typeof raw !== 'string') throw new Error('Empty Gemini response.');
		const result = coerceTranslatedResult<T>(extractJsonObject(raw), fields);
		result[source] = { ...result[source], ...fields };
		debugAutoTranslate('pre-check', 'H2', 'Gemini translate response validated', {
			sourceLanguage: source,
			rawLength: raw.length,
			locales: API_TRANSLATION_LOCALE_CODES,
			fieldPresence: API_TRANSLATION_LOCALE_CODES.map((locale) => ({
				language: locale,
				fields: Object.fromEntries(
					fieldKeys.map((keyName) => [keyName, Boolean(result[locale][keyName]?.trim())])
				),
			})),
		});
		if (process.env.NODE_ENV === 'development') {
			console.log('[entityAutoTranslate response]', result);
		}
		return result;
	} catch (error) {
		const errorDetails = {
			sourceLanguage: source,
			fieldKeys,
			nonEmptyFieldKeys,
			errorName: error instanceof Error ? error.name : typeof error,
			errorMessage: error instanceof Error ? error.message : String(error),
			errorData: (error as any)?.data,
			errorStatus: (error as any)?.status,
			errorStack: error instanceof Error ? error.stack : undefined,
		};
		debugAutoTranslate('pre-check', 'H2,H4', 'Gemini translate failed before payload build', errorDetails);
		if (process.env.NODE_ENV === 'development') {
			console.error('[entityAutoTranslate] Gemini API Error Details:', errorDetails);
		}
		throw new EntityAutoTranslateError('Gemini translation failed.', { cause: error });
	}
}
